/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SignUpPage
 * --------
 * This is the user registration page of the application responsible for signing up new users.
 *
 * Responsibilities:
 *  - Renders a reusable SignUpForm component for user input.
 *  - Manages user registration by sending user details along with organization and application IDs
 *    to the backend registration API endpoint.
 *  - Handles API responses, including errors, and displays error messages on the form.
 *  - Shows success confirmation in a modal dialog and navigates users to the sign-in page after registration.
 *  - Displays a warning alert dialog and redirects to sign-in if user registration is disabled via config.
 *
 * Key integrations:
 *  - Uses APIHelper instance configured for backend API communication.
 *  - Uses react-router's useNavigate hook for navigation after registration or when registration is disabled.
 *  - Utilizes environment configuration values including organization and application IDs and registration flag.
 *  - Uses AlertDialog component for modal alerts and confirmation dialogs.
 *
 * Usage:
 *  The SignUpPage is used as a route page in the application routing setup.
 *
 * Example:
 *  <Route path="/sign-up" element={<SignUpPage />} />
 *
 * Note:
 *  - The SignUpForm component must accept an `onSubmit` prop for handling form submission.
 *  - The registration availability is controlled by the `isUserRegistrationAllowed` flag from environment config.
 *  - Error messages are displayed on the SignUpForm via provided setErrors callback.
 *  - Success dialog appears only on successful registration and redirects to sign-in upon confirmation.
 */


import React, { useState } from 'react';
import SignUpForm from "../components/SignUpForm";
import PublicErrorPage from "../components/public/PublicErrorPage";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import { useNavigate } from "react-router-dom";
import { withNext } from "../utils/returnTo";
import AlertDialog from '../components/common/AlertDialog';
import { getPostAuthDestination } from '../utils/postAuthDestination';
import { DEFAULT_DIAL_CODE, normaliseNumber } from '../components/athlohive/event-detail/countryDialCodes';


const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});


const organizationId = envConfig.organizationId;
const applicationId = envConfig.applicationId;


function SignUpPage() {
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');

    if (!envConfig.isUserRegistrationAllowed) {
        return <PublicErrorPage error={{ status: 404 }} />;
    }


    // const handleSignUp = async ({ firstName, lastName, username, email, password }, setErrors) => {
    const handleSignUp = async ({ firstName, lastName, username, email, mobile, password }, setErrors) => {
        try {
            const bodyPayload = {
                organization: organizationId,
                applications: [applicationId],
                username,
                email,
                password,
                first_name: firstName,
                last_name: lastName,
                // Fixed to India for now. Stored as '+91' with the plus, the
                // same format ApplicationMembership.country_code and the event
                // entry modal already use.
                country_code: DEFAULT_DIAL_CODE,
                mobile_number: normaliseNumber(mobile),
            };


            const response = await apiHelper.apiCall("/arcee/user/register/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(bodyPayload),
            });


            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                // setErrors({ confirmPassword: err.error || err.message || "Registration failed" });
                // The server answers with DRF field errors ({"email": ["..."]}); show each
                // under its own field instead of one generic message under Confirm Password.
                const FIELD = { first_name: 'firstName', last_name: 'lastName', username: 'username', email: 'email', mobile_number: 'mobile', country_code: 'mobile', password: 'password' };
                const text = (v) => (Array.isArray(v) ? v.join(' ') : typeof v === 'string' ? v : '');
                const mapped = {};
                Object.entries(err || {}).forEach(([key, value]) => {
                    if (FIELD[key] && text(value)) mapped[FIELD[key]] = text(value);
                });
                if (!Object.keys(mapped).length) {
                    mapped.confirmPassword = err.error || err.message || text(err.non_field_errors) || text(err.detail) || "Registration failed";
                }
                setErrors(mapped);
                return;
            }


            const data = await response.json();
            setDialogMessage(
                `Registration successful. We've sent a verification email to ${email}. ` +
                'Please verify your email address, then sign in. ' +
                "Check your spam folder if you don't see it."
            );
            setDialogOpen(true);


            console.log("Registration successful:", data);
        } catch (error) {
            setErrors({ password: error.message || "Sign up failed." });
            console.error("Sign up error:", error);
        }
    };


    const handleDialogOk = () => {
        setDialogOpen(false);
        navigate(withNext('/sign-in'));
    };


    return (
        <>
            <SignUpForm onSubmit={handleSignUp} />
            <AlertDialog
                open={dialogOpen}
                title="Success"
                message={dialogMessage}
                type="success"
                okLabel="Continue"
                showCancel={false}
                onOk={handleDialogOk}
            />
        </>
    );

}


export default SignUpPage;
